import { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useAtomValue } from 'jotai/react';
import { useTranslation } from 'next-i18next';
import { NICKNAME_MAX_LENGTH } from 'src/components/AzarOnly/Login/constants';
import Input from 'src/components/Input';
import useCssMediaDevice from 'src/hooks/useCssMediaDevice';
import { isRegisterLoadingAtom } from 'src/stores/register/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { pushEvent } from 'src/utils/event-gateway';
import { Container as ContainerBase, LoadingIndicator, NextButton, NoticeText, Section, Title } from './styles';
import { StepProps } from './types';
const Container = styled(ContainerBase)`
  ${({
  theme
}) => theme.screenSize.desktop} {
    height: 532px;
  }
`;
const Wrap = styled(Section)`
  padding-top: 32px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const InputCount = styled.p`
  font-size: 13px;
  font-weight: 400;
  text-align: end;

  ${({
  theme
}) => theme.screenSize.desktop} {
    font-size: 14px;
  }
`;
const CurrentCount = styled.span<{
  isValid: boolean;
}>`
  color: ${({
  isValid,
  theme
}) => isValid ? theme.color.primary1 : theme.color.accentA1};
`;
const Footer = styled(Section)`
  margin-block-start: auto;
`;
interface Props extends StepProps<string> {
  defaultValue?: string;
}
const NicknameStep = ({
  defaultValue = '',
  onNext,
  className
}: Props) => {
  const {
    t
  } = useTranslation();
  const isRegisterLoading = useAtomValue(isRegisterLoadingAtom);
  const [nickname, setNickname] = useState(defaultValue);
  const isMobile = useCssMediaDevice() === 'mobile';
  const registerWithNickname = useCallback(async () => {
    pushEvent({
      eventType: EVENT_TYPE.SIGN_UP,
      eventName: EVENT_NAME.SIGNUP__CLICK_NEXT_NAME,
      eventParams: {
        action_category: 'click',
        tab: 'signup',
        page: 'name',
        target: 'next',
        first_name: nickname
      }
    });
    onNext(nickname);
  }, [onNext, nickname]);
  const handleInput = (text: string) => {
    setNickname(text);
  };
  useEffect(() => {
    pushEvent({
      eventType: EVENT_TYPE.SIGN_UP,
      eventName: EVENT_NAME.SIGNUP__IMPRESSION_NAME,
      eventParams: {
        action_category: 'impression',
        tab: 'signup',
        page: 'name',
        target: 'name'
      }
    });
  }, []);
  const canRegisterNickname = nickname && nickname.length <= NICKNAME_MAX_LENGTH;
  return <Container className={className} data-sentry-element="Container" data-sentry-component="NicknameStep" data-sentry-source-file="Nickname.tsx">
      <Section data-sentry-element="Section" data-sentry-source-file="Nickname.tsx">
        <Title data-sentry-element="Title" data-sentry-source-file="Nickname.tsx">{t('signup__info_name_title')}</Title>
        <NoticeText data-sentry-element="NoticeText" data-sentry-source-file="Nickname.tsx">{t('signup__info_name_desc')}</NoticeText>
      </Section>
      <Wrap data-sentry-element="Wrap" data-sentry-source-file="Nickname.tsx">
        <Input type='text' value={nickname} maxLength={NICKNAME_MAX_LENGTH} onChangeText={handleInput} label={t('signup__info_name_guide')} placeholder={t('signup__info_name_placeholder')} autoFocus={!isMobile} defaultValue={nickname} data-sentry-element="Input" data-sentry-source-file="Nickname.tsx" />
        <InputCount data-sentry-element="InputCount" data-sentry-source-file="Nickname.tsx">
          <CurrentCount isValid={(nickname?.length ?? 0) <= NICKNAME_MAX_LENGTH} data-sentry-element="CurrentCount" data-sentry-source-file="Nickname.tsx">
            {nickname?.length ?? 0}
          </CurrentCount>
          /{NICKNAME_MAX_LENGTH}
        </InputCount>
      </Wrap>
      <Footer data-sentry-element="Footer" data-sentry-source-file="Nickname.tsx">
        <NextButton type='button' disabled={!canRegisterNickname || isRegisterLoading} onClick={registerWithNickname} data-sentry-element="NextButton" data-sentry-source-file="Nickname.tsx">
          {isRegisterLoading ? <LoadingIndicator /> : t('SIGNUP__NEXT')}
        </NextButton>
      </Footer>
    </Container>;
};
export default NicknameStep;